/**
 * Provider-agnostic exposure signal — emitted once per exposure, at the
 * moment the flag store's `markExposed` dedup transitions false → true.
 * `@axo/observability` subscribes to this (via `onExposure`, re-exported at
 * `@axo/feature-flags/exposure`) to drive the `'Experiment Exposure'`
 * Mixpanel/GA event — feature-flags itself never calls observability; this
 * is the reversed-dependency seam (MV-2251 Phase 2).
 *
 * Dependency direction is one-way, store → signal: `core/flag-store.ts`
 * pulls in `_emitExposure` and calls it at the dedup transition; consumers
 * subscribe via the `@axo/feature-flags/exposure` subpath. This module
 * depends on NOTHING and knows nothing about the store — which is what lets
 * the public subpath re-export from here without touching the private
 * store module.
 *
 * Audience: analytics INFRASTRUCTURE only (the observability bridge, a future
 * LD adapter). Product code reads flags and fires exposures via `flags()` /
 * the React hooks — it should never subscribe here: exposure reactions belong
 * to the one centralized `Experiment Exposure` event, not per-feature
 * listeners.
 */
export type ExposureEvent = {
  kind: 'experiment' | 'gate';
  key: string;
  variant?: unknown;
};

type ExposureListener = (event: ExposureEvent) => void;

const exposureListeners = new Set<ExposureListener>();

/** Subscribe to the exposure signal. Returns an unsubscribe function. */
export function onExposure(cb: ExposureListener): () => void {
  exposureListeners.add(cb);
  return () => {
    exposureListeners.delete(cb);
  };
}

/**
 * Internal emit — called ONLY by the flag store (`core/flag-store.ts`), on
 * the false → true dedup transition inside `markExposed`. Not re-exported
 * on the public subpath; consumers only ever subscribe.
 */
export function _emitExposure(event: ExposureEvent): void {
  exposureListeners.forEach((listener) => listener(event));
}
